import { useChatClient } from "@/modules/chat/stores/useChatStore"
import { type Page } from "@/modules/utils/paginated"
import { GetStreamUserEntity } from "@modules/chat/entities/getStreamUserEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { type ExploreProfileContentDto } from "@modules/explore/gateway/exploreDto"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const useGetStreamUsers = (searchTerms?: string) => {
  const { exploreApi } = useServicesContext()
  const chatClient = useChatClient()

  const { data, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: ["getStreamUsers", searchTerms],
    queryFn: async ({ pageParam }) => {
      const data: Page<ExploreProfileContentDto> = (await exploreApi.searchExplore(searchTerms, {
        filter: ExploreFilter.PROFILE,
        page: pageParam,
        size: 40,
      })) as Page<ExploreProfileContentDto>

      const ids: string[] = data.content.map((content) => content.content.id)

      const getStreamUsers =
        ids.length > 0
          ? await chatClient?.queryUsers(
              { $and: [{ id: { $in: ids } }, { role: "user" }] },
              { name: 1 },
              { presence: true },
            )
          : undefined

      return {
        ...data,
        content: data.content.map((content) =>
          GetStreamUserEntity.fromDto({
            ...content.content,
            last_active: getStreamUsers?.users.find((user) => user.id === content.content.id)?.last_active,
            online: getStreamUsers?.users.find((user) => user.id === content.content.id)?.online,
          }),
        ),
      }
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages - 1) {
        return undefined
      }
      return lastPage.page + 1
    },
  })

  const itemList = useMemo(() => data.pages.flatMap((page) => page.content), [data])

  return {
    data: {
      ...data,
      content: itemList,
      totalSize: data.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}
